import { z } from "zod";
import type { Enums } from "../../../types";

/**
 * The reasons a movement may be booked by hand.
 *
 * `pick` is absent on purpose rather than filtered out of `transactionTypes`: writing the set out
 * is what makes the omission visible at the point it matters, and `satisfies` still fails to
 * compile if the database enum drops one of these.
 */
const HAND_BOOKED_TRANSACTION_TYPES = [
	"restock",
	"return",
	"inventory",
	"correction",
] as const satisfies ReadonlyArray<Enums<"transactionType">>;

export const getArticleLocationParamSchema = z.object({
	articleLocationId: z.uuid(),
});

export type GetArticleLocationParamSchema = z.infer<typeof getArticleLocationParamSchema>;

/**
 * One hand-booked article movement on a single shelf: material came back off a site, a count
 * corrected the figure, a delivery restocked the bin.
 *
 * This is the first write path `articleLocationStockTransactions` has other than a picking round.
 * Until it existed, `Orders.insertSuborderPick` was the only writer in the codebase and only ever
 * wrote `REMOVE`/`pick`, which left `return`, `inventory`, `correction` and `restock` declared in
 * the enum and reachable from nowhere.
 *
 * `operation` is separate from a signed quantity for the reason the carrier schema states: the
 * balance triggers read `ADD`/`REMOVE`, and a negative quantity would sail past them.
 *
 * `pick` is deliberately **not** accepted. A pick settles an order line as well as moving stock,
 * and only `insertSuborderPick` does both — booking one here would move the shelf and leave the
 * order still owing the goods.
 *
 * `returnOrderId` is the site the material came back off, and it is optional because most reasons
 * have no site behind them: an inventory count corrects a shelf to what is physically on it, and
 * naming a project there would put a movement that never left the building into that project's
 * reconciliation.
 */
export const postArticleLocationTransactionJsonSchema = z.object({
	operation: z.enum(["ADD", "REMOVE"]),
	quantity: z.coerce.number().positive(),
	type: z.enum(HAND_BOOKED_TRANSACTION_TYPES),
	returnOrderId: z.uuid().optional(),
});

export type PostArticleLocationTransactionJsonSchema = z.infer<
	typeof postArticleLocationTransactionJsonSchema
>;
